import React, { useEffect, useMemo } from "react";
import Measure from 'react-measure';
import { useDispatch, useSelector } from 'react-redux';
import { replace } from 'connected-react-router';

import { DataGrid, ProgressIndicator, Toolbar, Pager, Button } from "@flashtalking/rhombus-react";

import { DisabledExistingCreativesRenderer } from 'utils/columns/DisabledExistingCreativesRow';
import {
	MIN_GRID_HEIGHT
	, DEFAULT_ROW_HEIGHT
	, SEARCH_DEBOUNCE
	, CREATIVE_ROW_COUNT_OPTIONS
	, HTML_CONCEPT_TYPE
} from "utils/constants";
import { ConditionalWrapper } from 'utils/common';
import { Existing } from "reducers/creatives/ExistingCreatives";
import { clearLevel } from "reducers/Selected";
import { useCollectionFiltering } from "utils/DataGrid";
import { EXISTING_CREATIVES_GRID_COLUMNS } from 'widgets/grid/columns/Creatives';
import EmptySection from "widgets/EmptySection";
import { CONCEPT_NAME } from "widgets/forms/list";
import { saveConcept } from 'reducers/ConceptDetails';
import { loadCreatives } from "reducers/Creatives";

import './ExistingCreativesGrid.css';

export function buildSelectedDimensionsList(creatives, selectedCreativeIds){
	let selectedDimensions = new Set();

	selectedCreativeIds.forEach((creativeId) => {
		let selectedCreative = creatives.find(v => v.id === creativeId);
		const { width, height } = selectedCreative;

		selectedDimensions.add(`${width}x${height}`);
	});

	return selectedDimensions;
}

export default function ExistingCreativesGrid(props){
	const { libraryId, returnPath, formattedExistingSizes } = props
		, dispatch = useDispatch()
		, {
			existingCreatives,
			creatives,
			selectedCreativeIds,
			formValues,
		} = useSelector((state) => {
			return {
				existingCreatives: state.existingCreatives,
				creatives: state.existingCreatives?.items || [],
				selectedCreativeIds: state.selected?.existingCreatives || [],
				formValues: state.form?.['open-concept']?.values,
			};
		})
		, hasItems = creatives.length;

	const {
		filterInfo: {
			page, rpp,
			searchTerm,
			sort },
	} = useCollectionFiltering(
		existingCreatives,
		Existing.setAdvancedFilter,
		() => {
			dispatch(clearLevel('existingCreatives'));
			dispatch(Existing.loadCreatives(libraryId, formattedExistingSizes));
		}
	);

	let [sortColumn, sortDirection] = sort.split(":");
	const dataGridProps = {
		columns: EXISTING_CREATIVES_GRID_COLUMNS,
		rowGetter:  getCreativeRow,
		rowsCount:  creatives.length,
		rowHeight:  DEFAULT_ROW_HEIGHT,
		sortColumn,
		sortDirection,
		rowRenderer: DisabledExistingCreativesRenderer,
		onGridSort: (col, dir) => dispatch(Existing.setSort(col, dir)),
	};
	const pagerProps = {
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: existingCreatives.totalPages,
		maxItems: existingCreatives.totalItems,
		showItemWord: "",
		rowCountItems: CREATIVE_ROW_COUNT_OPTIONS,
		disabled: !existingCreatives.isLoaded,

		onPageChange: newPage => dispatch(Existing.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(Existing.setRPP(newRPP)),
	};

	useEffect(() => {
		return () => {
			clearSearch();
		};
	}, []);

	const clearSearch = () => {
		return dispatch(Existing.setSearchTerm(""));
	};

	const emptySectionProps = {
		header: "No search results to display",
		message:"Try adjusting your search to find what you are looking for",
		src:"/images/noSearch.svg",
		buttons:[<Button onClick={() => clearSearch()} key="btn-clear">Clear Search</Button>],
	};

	function mapCreatives(creatives, selectedCreativeIds){
		let selectedDimensions = buildSelectedDimensionsList(creatives, selectedCreativeIds);

		return creatives.map((creative) => {
			const {
				id,
				width,
				height,
			} = creative;
			let canSelect = false;

			const totalFileSize = creative?.totalFileSize?.totalSize
				 , isSelected = selectedCreativeIds.includes(creative.id);

			if(isSelected){
				canSelect = true;
			} else {
				if(!selectedDimensions.has(`${width}x${height}`)){
					canSelect = true;
				}
			}

			return {
				...creative,
				totalFileSize,
				actions: id,
				canSelect,
				// TODO: is this the best way to figure this out?  Maybe if we upgrade the grid we can use it's built in functionality
				isSelected,
			};
		});
	}

	const augmentedCreatives = useMemo(() => {
		return mapCreatives(creatives, selectedCreativeIds);
	}, [creatives, selectedCreativeIds]);

	function getCreativeRow(rowID) {
		return augmentedCreatives[rowID];
	}

	const makeConceptExisting = (e) => {
		e.preventDefault();

		let conceptData = {
			conceptName: formValues.conceptName,
			conceptType: HTML_CONCEPT_TYPE,
			creativeLibraryId: libraryId,
			creativeIds: selectedCreativeIds,
		};

		dispatch(saveConcept(libraryId, conceptData)).then((resp) => {
			if(resp?.success) {
				dispatch(loadCreatives(libraryId));
				dispatch(replace(returnPath));
			}
		});
	};

	const renderBody = (gridHeight) => {
		return(
			<ConditionalWrapper condition={formValues}
				wrapper={children => <form id={CONCEPT_NAME} onSubmit={makeConceptExisting}>{children}</form> }>
				<div>
					{!hasItems && existingCreatives.isLoaded ? <EmptySection {...emptySectionProps} /> : <DataGrid
						{ ...dataGridProps }
						minHeight={ gridHeight }
								 /> }
				</div>
			</ConditionalWrapper>
		);
	};

	return (
		<div className="existing-creatives-grid">
			<ProgressIndicator isLoading={!existingCreatives.isLoaded} />
			<Toolbar
				search
				onSearch={value => dispatch(Existing.setSearchTerm(value))}
				className="creatives-search"
				searchDebounce={SEARCH_DEBOUNCE}
				searchValue={searchTerm}
				searchProps={{ placeholder: "Search Creative" }}
			/>
			<Measure>
				{ (obj) => {
					let gridHeight = obj?.contentRect?.entry?.height || MIN_GRID_HEIGHT;
					return renderBody(gridHeight);
				} }
			</Measure>
			{hasItems ? <Pager {...pagerProps} /> : null}
		</div>
	);
}

ExistingCreativesGrid.displayName = "ExistingCreativesGrid";
